import type { ListTeamMembersFn } from '@/features/iam'
import type { AlertFactory } from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { required, validationContext } from 'validx'

/**
 * View model for the dialog where a client team member adds teammates as
 * supporting clients.
 */
export class AddClientsAsTeamMemberDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Team member to add as a supporting client.
   */
  @observable accessor client: Member | null | undefined = undefined

  /**
   * Disables submit button.
   */
  @observable accessor disabled = true

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    client: [required('Collaborator is required')],
  })

  constructor(
    private readonly _job: Job,
    private readonly _canAddClientsAsTeamMember: () => boolean,
    private readonly _jobStore: JobStore,
    private readonly _listTeamMembers: ListTeamMembersFn,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }

  /**
   * Sets the client to add.
   */
  @action.bound
  setClient(client?: Member | null) {
    this.client = client
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.client = undefined
    this.disabled = true

    this.submit.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Adds the selected team member as a supporting client.
   */
  submit = task.resolved(async () => {
    if (!this._canAddClientsAsTeamMember()) {
      this._newAlert()
        .withState('error')
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
        .show()
      return
    }

    if (!this.validate()) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Adding Client',
        `Adding ${this.client?.displayName} to ${this._job.name}...`,
      )
      .autoDismiss()
      .show()

    try {
      await this._jobStore.addTeamMemberAsSupportingClient(
        this._job.id,
        this.client?.user.publicId,
      )
    } catch (err) {
      alert.dismiss()
      throw err
    }

    alert
      .withState('success')
      .saying(
        'Success!',
        `${this.client?.displayName} was added to ${this._job.name}.`,
      )
      .autoDismiss()

    this.modal.close()
  })

  /**
   * Fetches team members.
   */
  fetchTeamMembers = async (
    _searchText: string,
    params: Partial<CursorPageQuery>,
  ): Promise<CursorPaged<Member | null>> => {
    if (!this._job.ownerTeam) {
      return CursorPaged.empty()
    }

    const collaborators = this._job.collaborators
    const paged = await this._listTeamMembers({
      after: params.after,
      teamId: this._job.ownerTeam.id,
    })

    return CursorPaged.map(paged, (data) =>
      data
        .filter((m) => {
          const invalidCandidate = collaborators.some(
            (s) => s.member === m.member,
          )
          return !invalidCandidate
        })
        .map((m) => m.member),
    )
  }
}
